import React from "react";

// region External libraries
import { Button } from "@libraries/mui/components";
// endregion External libraries
// region Languages
import useTranslation from "@languages/useTranslation";
// endregion Languages

import { LegacyMigrationMessages } from "../languages/interfaces";
import * as Styled from "../styles";
import { MigrationProgress } from "../useLegacyMigration";

interface IFinishedProps {
  tenantName: string;
  progress: MigrationProgress;
  busy: boolean;
  onExport: () => void;
  onRetry: () => void;
  onLeave: () => void;
}

/**
 * The last step — what happened. A run that stopped halfway, or ended with
 * errors, can be run again: provisioning matches by e-mail, so nobody is
 * duplicated, and the next review shows who is already in Identity.
 */
const Finished: React.FC<IFinishedProps> = ({
  tenantName, progress, busy, onExport, onRetry, onLeave
}) => {

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks

  const incomplete = progress.done < progress.total;
  const wrong = incomplete || progress.errors > 0;

  return (
    <Styled.Card>
      <Styled.Centered>
        <Styled.Outcome $tone={wrong ? "bad" : "ok"}>{wrong ? "!" : "✓"}</Styled.Outcome>

        <Styled.StepTitle>
          {t((() => {
            if (incomplete) return LegacyMigrationMessages.legacyMigrationStoppedTitle;

            return progress.errors > 0
              ? LegacyMigrationMessages.legacyMigrationDoneWithErrorsTitle
              : LegacyMigrationMessages.legacyMigrationDoneTitle;
          })(), { tenant: tenantName })}
        </Styled.StepTitle>

        <Styled.Stats>
          <Styled.Stat $tone="ok">
            <b>{progress.created}</b>
            <span>{t(LegacyMigrationMessages.legacyMigrationStatCreated)}</span>
          </Styled.Stat>
          <Styled.Stat $tone="info">
            <b>{progress.updated}</b>
            <span>{t(LegacyMigrationMessages.legacyMigrationStatUpdated)}</span>
          </Styled.Stat>
          <Styled.Stat>
            <b>{progress.skipped}</b>
            <span>{t(LegacyMigrationMessages.legacyMigrationStatSkipped)}</span>
          </Styled.Stat>
          <Styled.Stat $tone={progress.errors > 0 ? "bad" : undefined}>
            <b>{progress.errors}</b>
            <span>{t(LegacyMigrationMessages.legacyMigrationStatErrors)}</span>
          </Styled.Stat>
        </Styled.Stats>

        <Styled.StepHint>
          {t(LegacyMigrationMessages.legacyMigrationDoneHint, {
            kept: progress.passwordsPreserved,
            reset: progress.resetEmailsSent
          })}
          {progress.legacyLocked > 0 && (
            <>
              {" "}
              {t(LegacyMigrationMessages.legacyMigrationLockedSummary, { count: progress.legacyLocked })}
            </>
          )}
        </Styled.StepHint>

        {/* A lock that did not happen leaves that user able to use the old
            login: the operator has to know, and the CSV says why per user. */}
        {progress.legacyLockFailed > 0 && (
          <Styled.Banner $tone="warning">
            {t(LegacyMigrationMessages.legacyMigrationLockFailed, { count: progress.legacyLockFailed })}
          </Styled.Banner>
        )}

        <Styled.Actions>
          <Button disabled={busy || progress.done === 0} onClick={onExport}>
            {t(LegacyMigrationMessages.legacyMigrationExport)}
          </Button>
          {wrong && (
            <Button disabled={busy} onClick={onRetry}>
              {t(LegacyMigrationMessages.legacyMigrationRetry)}
            </Button>
          )}
          <Styled.Spacer />
          <Button variant="contained" disabled={busy} onClick={onLeave}>
            {t(LegacyMigrationMessages.legacyMigrationLeave)}
          </Button>
        </Styled.Actions>
      </Styled.Centered>
    </Styled.Card>
  );
};

export default Finished;
